import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

//- Material UI / Style
import { withStyles } from '@material-ui/core/styles';

//containers
import AuthUserContainer from '../../containers/AuthUser';
import UserAssignmentContainer from '../../containers/UserAssignment';
import EquipmentAssignmentContainer from '../../containers/EquipmentAssignment';

//- UI / Form
import clsx from 'classnames';
import { getAssignmentBreakdowns } from './assignmentHelper';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import { getGlobalClasses } from '../../themes';

const DAY_SHIFT_ID = 1;
// const NIGHT_SHIFT_ID = 2;

const styles = (theme) => {
	return {
		...getGlobalClasses(theme),
		pickToShow: {
			padding: 20,
			background: '#f7f7f7',
			border: '1px solid #ddd',
			color: '#333',
			borderRadius: 2,
		},
		notCurrentlyAssigned: {
			fontSize: 20,
			borderBottom: '1px solid #ddd',
			paddingBottom: 5,
			marginBottom: 10,
		},
		assignmentHelper: {
			padding: 20,
			border: '1px solid #eee',
			margin: 10,
			marginTop: 40,
			backgroundColor: '#f7f7f7',
			borderRadius: 5,
		},
		notSelectedLocation: {
			opacity: 0.3,
		},
		selectedLocation: {
			borderLeft: '3px solid #1289d4',
		},
		locationContainer: {
			padding: 10,
			marginBottom: 30,
		},
		locationHeader: {
			borderBottom: '1px solid #ddd',
			paddingBottom: 5,
			marginBottom: 10,
		},
		location: {
			fontSize: '200%',
			paddingRight: 10,
		},
		locationCount: {
			fontWeight: 200,
			fontSize: '120%',
			color: '#333',
		},

		locationEquipmentCount: {
			fontWeight: 200,
			fontSize: '120%',
			color: '#333',
			marginLeft: 15,
		},
		locationTitle: {
			textTransform: 'uppercase',
			letterSpacing: 2,
			fontSize: 13,
			borderBottom: '1px solid #ddd',
			marginBottom: 20,
		},
		shiftTime: {
			marginLeft: 20,
		},
		shiftTitle: {
			fontSize: '120%',
			marginLeft: 0,
			display: 'flex',
			alignItems: 'center',
		},
		shiftMarker: {
			width: 15,
			height: 15,
			border: '1px solid white',
			borderRadius: 20,
			marginRight: 5,
		},
		assignedSection: {
			marginLeft: 20,
			marginTop: 15,
		},
		assignedTitle: {
			textTransform: 'uppercase',
			letterSpacing: 1,
			fontWeight: 500,
			fontSize: 15,
			color: '#666',
			borderBottom: '1px solid #ddd',
			paddingBottom: 2,
			marginBottom: 3,
		},

		shiftTimeTruck: {
			marginLeft: 20,
			display: 'flex',
		},
		truckInfo: {
			color: '#555',
			fontWeight: 500,
			marginLeft: 5,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		smallSemiTruckIcon: {
			display: 'inline-block',
			width: 11,
			height: 11,
			verticalAlign: 'middle',
			marginRight: 4,
		},
	};
};

/*
const globalData = [
	{ name: 'Group A', value: 14 },
	{ name: 'Group B', value: 11 },
];
 */

function _rgb({ r, g, b }) {
	return `rgb(${r}, ${g}, ${b})`;
}

const TYPE_COLORS = [
	_rgb({ r: 149, g: 117, b: 205 }),
	_rgb({ r: 100, g: 181, b: 246 }),
	_rgb({ r: 174, g: 213, b: 129 }),
	_rgb({ r: 255, g: 183, b: 77 }),
	_rgb({ r: 255, g: 138, b: 101 }),
	_rgb({ r: 229, g: 115, b: 115 }),
	_rgb({ r: 186, g: 104, b: 200 }),
];

const SHIFT_COLORS = [_rgb({ r: 205, g: 167, b: 12 }), _rgb({ r: 65, g: 92, b: 118 })];

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = (p) => {
	const { cx, cy, midAngle, innerRadius, outerRadius, percent } = p;
	const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
	const x = cx + radius * Math.cos(-midAngle * RADIAN);
	const y = cy + radius * Math.sin(-midAngle * RADIAN);

	return (
		<text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
			{`${(percent * 100).toFixed(0)}%`}
		</text>
	);
};

class TerminalAssignmentSummary extends Component {
	constructor(props) {
		super(props);

		this.state = {
			chartRows: undefined,
			assignmentBreakdowns: undefined,
		};
	}

	componentDidMount() {
		const {
			userAssignments,
			searchUserAssignments,
			equipmentAssignments,
			searchEquipmentAssignments,
		} = this.props;

		if (_.isEmpty(userAssignments)) {
			searchUserAssignments();
		}
		if (_.isEmpty(equipmentAssignments)) {
			searchEquipmentAssignments();
		}

		if (
			!_.isEmpty(userAssignments) &&
			!_.isEmpty(equipmentAssignments) &&
			_.isUndefined(this.state.assignmentBreakdowns)
		) {
			this.formatAssignmentBreakdowns();
		}
	}

	componentDidUpdate(prevProps, prevState, snapShot) {
		const { userAssignments, equipmentAssignments } = this.props;

		if (
			!_.isEmpty(userAssignments) &&
			!_.isEmpty(equipmentAssignments) &&
			_.isUndefined(this.state.assignmentBreakdowns)
		) {
			this.formatAssignmentBreakdowns();
		}
	}

	formatAssignmentBreakdowns = () => {
		const { userMap, locationMap, userAssignments, equipmentAssignments, locationId } = this.props;

		const { assignmentBreakdowns } = getAssignmentBreakdowns({
			userMap,
			locationMap,
			userAssignments,
			equipmentAssignments,
			locationId,
		});

		const chartRows = [];
		assignmentBreakdowns.forEach((assignmentBreakdown) => {
			const { location, count, equipmentCount, shiftRows } = assignmentBreakdown;

			const row = {
				location,
				count,
				equipmentCount,
			};

			let totalDay = 0;
			let totalNight = 0;
			const scheduleTypeData = {};

			shiftRows.forEach((shiftRow) => {
				const { shiftId, count, scheduleStatRows } = shiftRow;

				if (shiftId === DAY_SHIFT_ID) {
					totalDay += count;
				} else {
					totalNight += count;
				}

				//
				scheduleStatRows.forEach((scheduleStatRow) => {
					const { userAssignmentTypeId, scheduleDisplay, count } = scheduleStatRow;

					if (!scheduleTypeData[userAssignmentTypeId]) {
						scheduleTypeData[userAssignmentTypeId] = {
							userAssignmentTypeId,
							name: scheduleDisplay,
							value: 0,
						};
					}

					scheduleTypeData[userAssignmentTypeId].value += count;
				});
			});

			row.scheduleTypeData = _.sortBy(_.values(scheduleTypeData), 'name');
			row.data = [
				{ name: 'Day Shift', value: totalDay },
				{ name: 'Night Shift', value: totalNight },
			];
			chartRows.push(row);
		});

		this.setState({ chartRows, assignmentBreakdowns });
	};

	render() {
		const {
			classes,
			//equipmentMap,
		} = this.props;

		const { chartRows } = this.state;

		if (_.isUndefined(chartRows)) {
			return <div>Fetching breakdowns..</div>;
		}

		return (
			<>
				{chartRows.map((chartRow) => {
					const { location, count, data, scheduleTypeData, equipmentCount } = chartRow;
					const { locationId } = location;

					return (
						<Grid item xs={12} md={6} key={locationId}>
							<Paper className={classes.globalMainPaper}>
								<div className={clsx(classes.locationContainer)} key={locationId}>
									<div className={classes.locationHeader}>
										<span className={classes.location}>{location.display}</span>
										<span className={classes.locationCount}>{count} drivers</span>
										<span className={classes.locationEquipmentCount}>{equipmentCount} trucks</span>
									</div>

									<div style={{ display: 'flex' }}>
										<div style={{ width: '100%', height: '310px' }}>
											<ResponsiveContainer>
												<PieChart>
													<Pie
														data={data}
														cx={130}
														cy={130}
														labelLine={false}
														label={renderCustomizedLabel}
														outerRadius={120}
														fill="#8884d8"
														dataKey="value"
													>
														{data.map((entry, index) => (
															<Cell
																key={`cell-${index}`}
																fill={SHIFT_COLORS[index % SHIFT_COLORS.length]}
															/>
														))}
													</Pie>
													<Legend
														layout="vertical"
														verticalAlign="bottom"
														align="center"
														height={36}
													/>
												</PieChart>
											</ResponsiveContainer>
										</div>

										<div style={{ width: '100%', height: '310px' }}>
											<ResponsiveContainer>
												<PieChart>
													<Pie
														data={scheduleTypeData}
														cx={130}
														cy={130}
														labelLine={false}
														label={renderCustomizedLabel}
														outerRadius={120}
														fill={_rgb({ r: 174, g: 213, b: 129 })}
														dataKey="value"
													>
														{data.map((entry, index) => (
															<Cell
																key={`cell-${index}`}
																//fill={TYPE_COLORS[index % TYPE_COLORS.length]}
																fill={TYPE_COLORS[index]}
															/>
														))}
													</Pie>
													<Legend
														layout="vertical"
														verticalAlign="bottom"
														align="center"
														height={36}
													/>
												</PieChart>
											</ResponsiveContainer>
										</div>
									</div>
								</div>
							</Paper>
						</Grid>
					);
				})}
			</>
		);
	}
}

TerminalAssignmentSummary.propTypes = {
	searchUserAssignments: PropTypes.func.isRequired,
	userAssignments: PropTypes.array,
	searchEquipmentAssignments: PropTypes.func.isRequired,
	equipmentAssignments: PropTypes.array,
	classes: PropTypes.object.isRequired,
	showShifts: PropTypes.bool,
	locationId: PropTypes.number,
};

export default _.flow([
	withStyles(styles),
	AuthUserContainer,
	UserAssignmentContainer,
	EquipmentAssignmentContainer,
])(TerminalAssignmentSummary);
